import { FloorplanArea, FloorplanDesk, FloorplanLevel } from '../../types/floorplan';

export type PlacementTool = 'desk' | 'office' | 'meeting_room' | 'wall' | 'door' | 'open_area';
export type EditorSelection = { kind: 'desk' | 'area'; id: string };
export interface Rect { x: number; y: number; width: number; height: number }
export interface LayoutHistory { past: FloorplanLevel[]; present: FloorplanLevel; future: FloorplanLevel[] }
export type HistoryAction = { type: 'commit'; level: FloorplanLevel } | { type: 'reset'; level: FloorplanLevel } | { type: 'undo' | 'redo' };

export const GRID_SIZE = 10;
export const MAX_DESKS = 1000;
export const MAX_AREAS = 500;
const MAX_HISTORY = 50;

export const cloneLevel = (level: FloorplanLevel): FloorplanLevel => ({
  ...level,
  desks: level.desks.map(desk => ({ ...desk })),
  areas: level.areas.map(area => ({ ...area })),
});

export const createHistory = (level: FloorplanLevel): LayoutHistory => ({ past: [], present: cloneLevel(level), future: [] });
export const layoutHistoryReducer = (state: LayoutHistory, action: HistoryAction): LayoutHistory => {
  if (action.type === 'reset') return createHistory(action.level);
  if (action.type === 'undo') {
    if (!state.past.length) return state;
    return { past: state.past.slice(0, -1), present: state.past[state.past.length - 1], future: [state.present, ...state.future] };
  }
  if (action.type === 'redo') {
    if (!state.future.length) return state;
    return { past: [...state.past, state.present].slice(-MAX_HISTORY), present: state.future[0], future: state.future.slice(1) };
  }
  if (action.type === 'commit' && JSON.stringify(state.present) !== JSON.stringify(action.level)) {
    return { past: [...state.past, state.present].slice(-MAX_HISTORY), present: action.level, future: [] };
  }
  return state;
};

export const snap = (value: number, enabled: boolean) => enabled ? Math.round(value / GRID_SIZE) * GRID_SIZE : Math.round(value);
export const canvasPoint = (clientX: number, clientY: number, bounds: Pick<DOMRect, 'left' | 'top'>, zoom: number) => ({
  x: (clientX - bounds.left) / zoom,
  y: (clientY - bounds.top) / zoom,
});

export const fitZoom = (level: Pick<FloorplanLevel, 'width' | 'height'>, viewportWidth: number, viewportHeight: number) =>
  Math.max(0.05, Math.min(3, (viewportWidth - 48) / level.width, (viewportHeight - 48) / level.height));

export const boundedRect = (rect: Rect, level: Pick<FloorplanLevel, 'width' | 'height'>): Rect => {
  if (![rect.x, rect.y, rect.width, rect.height].every(Number.isFinite)) throw new Error('Coordinates and dimensions must be numbers.');
  const width = Math.max(4, Math.min(level.width, Math.round(rect.width)));
  const height = Math.max(4, Math.min(level.height, Math.round(rect.height)));
  return {
    x: Math.max(0, Math.min(level.width - width, Math.round(rect.x))),
    y: Math.max(0, Math.min(level.height - height, Math.round(rect.y))),
    width,
    height,
  };
};

export const selectedObject = (level: FloorplanLevel, selection: EditorSelection | null): FloorplanDesk | FloorplanArea | undefined => {
  if (!selection) return undefined;
  return selection.kind === 'desk' ? level.desks.find(item => item.id === selection.id) : level.areas.find(item => item.id === selection.id);
};

export const changeGeometry = (level: FloorplanLevel, selection: EditorSelection, patch: Partial<Rect>): FloorplanLevel => {
  const current = selectedObject(level, selection);
  if (!current) return level;
  const geometry = boundedRect({ ...current, ...patch }, level);
  return selection.kind === 'desk'
    ? { ...level, desks: level.desks.map(item => item.id === selection.id ? { ...item, ...geometry } : item) }
    : { ...level, areas: level.areas.map(item => item.id === selection.id ? { ...item, ...geometry } : item) };
};

export const moveObject = (level: FloorplanLevel, selection: EditorSelection, x: number, y: number, snapToGrid: boolean) =>
  changeGeometry(level, selection, { x: snap(x, snapToGrid), y: snap(y, snapToGrid) });

export const rotateObject = (level: FloorplanLevel, selection: EditorSelection): FloorplanLevel => {
  const object = selectedObject(level, selection);
  if (!object) return level;
  if (object.height > level.width || object.width > level.height) {
    throw new Error('This object cannot fit when rotated. Reduce its size or enlarge the floor first.');
  }
  return changeGeometry(level, selection, { width: object.height, height: object.width });
};

export const resizeFloor = (level: FloorplanLevel, width: number, height: number): FloorplanLevel => {
  if (![width, height].every(value => Number.isInteger(value) && value >= 100 && value <= 10000)) {
    throw new Error('Floor dimensions must be whole numbers between 100 and 10,000.');
  }
  if ([...level.desks, ...level.areas].some(item => item.x + item.width > width || item.y + item.height > height)) {
    throw new Error('Move or resize objects inside the new floor boundary before shrinking the floor.');
  }
  return { ...level, width, height };
};

export const newGeometryId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  const bytes = new Uint8Array(16);
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') crypto.getRandomValues(bytes);
  else bytes.forEach((_value, index) => { bytes[index] = Math.floor(Math.random() * 256); });
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, value => value.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

const nextDeskNumber = (level: FloorplanLevel) => {
  const used = new Set(level.desks.map(desk => desk.number));
  let number = 1;
  while (used.has(String(number))) number += 1;
  return String(number);
};
const newDeskKey = (level: FloorplanLevel, number: string, id: string) =>
  `${level.key.replace(/[^A-Za-z0-9_-]/g, '-').slice(0, 40) || 'floor'}-desk-${number}-${id.slice(0, 8)}`;

const templates: Record<PlacementTool, { label: string; width: number; height: number }> = {
  desk: { label: 'Desk', width: 60, height: 40 },
  office: { label: 'Office', width: 200, height: 160 },
  meeting_room: { label: 'Meeting room', width: 240, height: 180 },
  wall: { label: 'Wall', width: 160, height: 10 },
  door: { label: 'Door', width: 60, height: 10 },
  open_area: { label: 'Open area', width: 200, height: 140 },
};

export const addObject = (level: FloorplanLevel, tool: PlacementTool, point: { x: number; y: number }, snapToGrid: boolean, id = newGeometryId()) => {
  if (tool === 'desk' && level.desks.length >= MAX_DESKS) throw new Error(`This floor already has the maximum of ${MAX_DESKS} desks.`);
  if (tool !== 'desk' && level.areas.length >= MAX_AREAS) throw new Error(`This floor already has the maximum of ${MAX_AREAS} areas.`);
  const template = templates[tool];
  const geometry = boundedRect({
    x: snap(point.x - template.width / 2, snapToGrid),
    y: snap(point.y - template.height / 2, snapToGrid),
    width: template.width,
    height: template.height,
  }, level);
  if (tool === 'desk') {
    const number = nextDeskNumber(level);
    const desk: FloorplanDesk = { id, key: newDeskKey(level, number, id), number, ...geometry };
    return { level: { ...level, desks: [...level.desks, desk] }, selection: { kind: 'desk', id } as EditorSelection };
  }
  const area: FloorplanArea = { id, key: `area_${id}`, type: tool, label: template.label, ...geometry };
  return { level: { ...level, areas: [...level.areas, area] }, selection: { kind: 'area', id } as EditorSelection };
};

export const duplicateObject = (level: FloorplanLevel, selection: EditorSelection, id = newGeometryId()) => {
  const object = selectedObject(level, selection);
  if (!object) throw new Error('Select an object to duplicate.');
  const geometry = boundedRect({ ...object, x: object.x + 20, y: object.y + 20 }, level);
  if (selection.kind === 'desk') {
    if (level.desks.length >= MAX_DESKS) throw new Error(`This floor already has the maximum of ${MAX_DESKS} desks.`);
    const number = nextDeskNumber(level);
    return {
      level: { ...level, desks: [...level.desks, { ...object as FloorplanDesk, ...geometry, id, key: newDeskKey(level, number, id), number }] },
      selection: { kind: 'desk', id } as EditorSelection,
    };
  }
  if (level.areas.length >= MAX_AREAS) throw new Error(`This floor already has the maximum of ${MAX_AREAS} areas.`);
  const area = object as FloorplanArea;
  return {
    level: { ...level, areas: [...level.areas, { ...area, ...geometry, id, key: `area_${id}`, label: `${area.label} copy`.slice(0, 120) }] },
    selection: { kind: 'area', id } as EditorSelection,
  };
};

export const removeObject = (level: FloorplanLevel, selection: EditorSelection, assignedDeskIds: ReadonlySet<string>): FloorplanLevel => {
  if (selection.kind === 'desk') {
    if (assignedDeskIds.has(selection.id)) throw new Error('This desk has an employee assigned. Clear the assignment before removing it.');
    return { ...level, desks: level.desks.filter(item => item.id !== selection.id) };
  }
  return { ...level, areas: level.areas.filter(item => item.id !== selection.id) };
};

export const validateDraft = (level: FloorplanLevel): string | null => {
  if (!level.name.trim() || level.name.length > 120) return 'Give this floor a name of 1–120 characters.';
  if (![level.width, level.height].every(value => Number.isInteger(value) && value >= 100 && value <= 10000)) return 'Floor dimensions must be whole numbers between 100 and 10,000.';
  if (level.desks.length > MAX_DESKS || level.areas.length > MAX_AREAS) return 'This floor exceeds its desk or area limit.';
  const objects = [...level.desks, ...level.areas];
  if (objects.some(item => ![item.x, item.y, item.width, item.height].every(Number.isInteger) || item.x < 0 || item.y < 0 || item.width < 4 || item.height < 4 || item.x + item.width > level.width || item.y + item.height > level.height)) return 'Every object must stay inside the floor with a width and height of at least 4.';
  if (level.desks.some(item => !item.number.trim() || item.number.length > 40)) return 'Every desk needs a number or label of 1–40 characters.';
  if (level.areas.some(item => !item.label.trim() || item.label.length > 120)) return 'Every area needs a label of 1–120 characters.';
  return null;
};
